<template>
  <SpectreCard
    class="insights-vr-container"
    :style="backgroundStyles"
  >
    <SpectreCardContent>
      <SpectreHeading
        as="h2"
        size="xl"
        class="mb-sm titles-branding"
        :style="reportStore.isPublicReport && { color: brandSettings.text.heading.colour }"
      >
        {{ valuationRequest.header }}
      </SpectreHeading>
      <SpectreText
        class="body-text-branding"
        :style="reportStore.isPublicReport && { color: brandSettings.text.body.colour }"
      >
        {{ valuationRequest.body }}
      </SpectreText>
      <SpectreButton
        type="button"
        :class="[
          'mt-lg',
          (styled || reportStore.isPublicReport) && 'insights-button'
        ]"
        :style="primaryButtonStyles"
        @click="handleClick"
      >
        {{ valuationRequest.buttonText }}
      </SpectreButton>
    </SpectreCardContent>
  </SpectreCard>
  <SpectreModal
    :open="showValuationRequestModal"
    :size="valuationRequest.system === 'street' ? 'xl' : 'base'"
    @close="toggleValuationRequestModal(false)"
  >
    <SpectreModalHeader v-if="valuationRequest.system === 'street'">
      <SpectreModalTitle>
        Book a valuation
      </SpectreModalTitle>
    </SpectreModalHeader>
    <SpectreModalContent>
      <DialogTitle
        :class="[
          valuationRequest.system === 'street' && 'sr-only',
          'text-2xl font-bold text-center mb-sm titles-branding',
        ]"
        :style="{ color: brandSettings.text.heading.colour }"
      >
        Request a valuation
      </DialogTitle>
      <DialogDescription
        :class="[
          valuationRequest.system === 'street' && 'sr-only',
        ]"
        class="font-medium text-sm text-center mb-7 text-default-700 body-text-branding"
        :style="{ color: brandSettings.text.body.colour }"
      >
        Fill in your details and one of our team will be in touch.
      </DialogDescription>
      <template v-if="valuationRequest.system === 'property_reports'">
        <form
          class="grid grid-cols-2 gap-4"
          @submit.prevent="onSubmit"
        >
          <div
            v-if="valuationTypeOptions.length > 1"
            class="col-span-2"
          >
            <SpectreListbox v-model="selectedValuationType">
              <SpectreLabel>
                Valuation type
              </SpectreLabel>
              <SpectreListboxControl
                class="mt-2"
                :value="selectedValuationType.label"
              >
                <SpectreListboxOptions>
                  <SpectreListboxOption
                    v-for="option in valuationTypeOptions"
                    :key="option.id"
                    :value="option"
                  >
                    {{ option.label }}
                  </SpectreListboxOption>
                </SpectreListboxOptions>
              </SpectreListboxControl>
            </SpectreListbox>
            <SpectreErrorMessage
              v-if="errors?.valuation_type"
              class="mt-2"
            >
              {{ errors?.valuation_type }}
            </SpectreErrorMessage>
          </div>
          <div
            class="col-span-1 body-text-branding"
            :style="{ color: brandSettings.text.body.colour }"
          >
            <SpectreField>
              <SpectreLabel>
                First name
              </SpectreLabel>
              <SpectreInput
                v-model="form.first_name"
                class="mt-2"
                :invalid="!!errors?.first_name"
                required
                autocomplete="given-name"
              />
              <SpectreErrorMessage v-if="errors?.first_name">
                {{ errors?.first_name }}
              </SpectreErrorMessage>
            </SpectreField>
          </div>
          <div class="col-span-1">
            <SpectreField>
              <SpectreLabel>
                Last name
              </SpectreLabel>
              <SpectreInput
                v-model="form.last_name"
                class="mt-2"
                :invalid="!!errors?.last_name"
                required
                autocomplete="family-name"
              />
              <SpectreErrorMessage v-if="errors?.last_name">
                {{ errors?.last_name }}
              </SpectreErrorMessage>
            </SpectreField>
          </div>
          <div class="col-span-2">
            <SpectreField>
              <SpectreLabel>
                Email
              </SpectreLabel>
              <SpectreInput
                v-model="form.email"
                class="mt-2"
                :invalid="!!errors?.email"
                required
                inputmode="email"
                autocomplete="email"
              />
              <SpectreErrorMessage v-if="errors?.email">
                {{ errors?.email }}
              </SpectreErrorMessage>
            </SpectreField>
          </div>
          <div class="col-span-1">
            <SpectreField>
              <SpectreLabel>
                Contact number
              </SpectreLabel>
              <SpectreInput
                v-model="form.contact_number"
                class="mt-2"
                :invalid="!!errors?.contact_number"
                required
                autocomplete="tel"
                inputmode="tel"
              />
              <SpectreErrorMessage v-if="errors?.contact_number">
                {{ errors?.contact_number }}
              </SpectreErrorMessage>
            </SpectreField>
          </div>
          <div class="col-span-1">
            <SpectreField>
              <SpectreLabel>
                Preferred date
              </SpectreLabel>
              <SpectreInput
                v-model="form.preferred_valuation_date"
                class="mt-2"
                required
                :invalid="!!errors?.preferred_valuation_date"
                type="date"
                :min="dayjs().format('YYYY-MM-DD')"
              />
              <SpectreErrorMessage v-if="errors?.preferred_valuation_date">
                {{ errors?.preferred_valuation_date }}
              </SpectreErrorMessage>
            </SpectreField>
          </div>
          <div class="col-span-2">
            <SpectreField>
              <SpectreLabel>
                Address
              </SpectreLabel>
              <SpectreInputAddressSearch
                v-model="selectedProperty"
                class="mt-2"
                :config="{
                  hostUrl: page.props.config.kraken.search_url,
                  apiKey: page.props.config.kraken.api_key,
                }"
                :invalid="!!errors?.street_group_property_id"
                placeholder="Start typing your address"
              />
              <SpectreErrorMessage v-if="errors?.street_group_property_id">
                {{ errors?.street_group_property_id }}
              </SpectreErrorMessage>
            </SpectreField>
          </div>
          <div class="col-span-2">
            <SpectreButton
              type="submit"
              block
              size="xl"
              class="insights-button"
              :loading="form.processing"
              :style="primaryButtonStyles"
            >
              Request valuation
            </SpectreButton>
          </div>
        </form>
        <SpectreText
          color="secondary"
          class="pt-8 text-center text-xs"
        >
          Please note that this report is provided by Spectre who will send the above details to {{ reportData.account?.company?.name }} only. By submitting, you agree to the
          <SpectreTextLink
            href="https://spectre.uk.com/terms"
            class="underline"
          >
            terms and conditions
          </SpectreTextLink>
          and our privacy policy.
        </SpectreText>
      </template>
      <template v-if="valuationRequest.system === 'street' && (reportStore.bookViewingUrl || valuationRequest.streetValuationPageUrl)">
        <iframe
          title="Valuation booking form"
          :src="reportStore.bookViewingUrl ?? (valuationRequest.streetValuationPageUrl ?? undefined)"
          class="w-full h-[calc(100vh-9rem)]"
        />
      </template>
    </SpectreModalContent>
  </SpectreModal>
  <SpectreModal
    :open="showValuationRequestConfirmationModal"
    @close="toggleValuationRequestConfirmationModal(false)"
  >
    <div class="flex flex-col items-center gap-base px-lg pt-base">
      <img
        v-if="brandSettings.logos?.rectangle?.url ?? brandSettings.logos?.square?.url"
        :src="brandSettings.logos?.rectangle?.url ?? brandSettings.logos?.square?.url"
        alt=""
        class="object-contain w-1/2 max-h-20"
      >
      <div class="text-center space-y-sm">
        <DialogTitle
          class="text-base text-default-700 font-medium"
          :style="{ color: brandSettings.text.heading.colour }"
        >
          {{ valuationRequest.confirmationHeader }}
        </DialogTitle>
        <DialogDescription
          class="text-sm text-default-500"
          :style="{ color: brandSettings.text.body.colour }"
        >
          {{ valuationRequest.confirmationMessage }}
        </DialogDescription>
      </div>
      <SpectreButton
        class="insights-button"
        :style="primaryButtonStyles"
        @click="toggleValuationRequestConfirmationModal(false)"
      >
        Close
      </SpectreButton>
    </div>
  </SpectreModal>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useStorage, useToggle } from '@vueuse/core';
import { DialogDescription, DialogTitle } from '@headlessui/vue';
import {
  SpectreErrorMessage,
  SpectreInput,
  SpectreInputAddressSearch,
  SpectreModal,
  SpectreModalTitle,
  SpectreButton,
  SpectreCard,
  SpectreCardContent,
  SpectreModalContent,
  SpectreModalHeader,
  SpectreHeading,
  SpectreLabel,
  SpectreField,
  SpectreListbox,
  SpectreListboxControl,
  SpectreListboxOptions,
  SpectreListboxOption,
  SpectreText,
  SpectreTextLink,
  type ListboxOptionType,
} from '@agentsoftware/spectre-ui';
import { useForm, usePage } from '@inertiajs/vue3';
import { useReportStore } from '@/Stores/Report';
import dayjs from 'dayjs';
import ValuationRequestData = App.Data.Settings.ReportContent.ValuationRequestData;
import BrandSettingsData = App.Data.Settings.BrandSettingsData;
import ReportData = App.Data.Report.ReportData;
import CustomDomainData = App.Data.CustomDomainData;

const props = defineProps<{
  brandSettings: BrandSettingsData;
  valuationRequest: ValuationRequestData;
  customDomain?: CustomDomainData;
  styled?: boolean;
}>();

const page = usePage();

const reportStore = useReportStore();

const errors = computed(() => usePage().props.errors as Record<string, string>);
const reportData = usePage().props.reportData as ReportData;

const [showValuationRequestModal, toggleValuationRequestModal] = useToggle<boolean>(false);
const [showValuationRequestConfirmationModal, toggleValuationRequestConfirmationModal] = useToggle<boolean>(false);

const valuationTypeOptions = [
  { id: 'lettings', label: 'Lettings' },
  { id: 'sales', label: 'Sales' },
].filter((type) => props.valuationRequest[type.id as 'sales' | 'lettings']) as ListboxOptionType[];

const selectedValuationType = ref<ListboxOptionType>({
  id: 'Select valuation type',
  label: 'Select valuation type',
});

const personalDetails = useStorage<{
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
}>('personal-details', {
  firstName: '',
  lastName: '',
  email: '',
  contactNumber: '',
});

const selectedProperty = ref<string>();

const form = useForm({
  first_name: personalDetails.value.firstName,
  last_name: personalDetails.value.lastName,
  email: personalDetails.value.email,
  contact_number: personalDetails.value.contactNumber,
  preferred_valuation_date: '',
  street_group_property_id: '',
  number_of_bedrooms: 0,
  property_type: '',
  valuation_type: selectedValuationType.value.id,
});

const handleClick = () => {
  if (!reportStore.isPublicReport) {
    return;
  }

  toggleValuationRequestModal(true);
};

const domain = computed(() => (props.customDomain ? `https://${props.customDomain.url}` : page.props.app.url));

const onSubmit = () => {
  form.street_group_property_id = selectedProperty.value as string;
  form.valuation_type = selectedValuationType.value.id as string;

  form.post(
    `${domain.value}/reports/${reportData.id}/valuation-request/create`,
    {
      onSuccess: () => {
        toggleValuationRequestModal(false);
        form.reset();
        toggleValuationRequestConfirmationModal(true);
      },
    },
  );
};

const backgroundStyles = ref({
  '--backgroundColor': '#fff',
});

const primaryButtonStyles = computed(() => ({
  '--textColor': `${props.brandSettings.buttons.secondary.colour}`,
  '--hoverTextColor': `${props.brandSettings.buttons.secondary.hoverColour}`,
  '--backgroundColor': `${props.brandSettings.buttons.secondary.backgroundColour}`,
  '--hoverBackgroundColor': `${props.brandSettings.buttons.secondary.backgroundHoverColour}`,
  '--fontFamily': `${props.brandSettings.buttons.secondary.fontFamily}`,
  '--fontWeight': `${props.brandSettings.buttons.secondary.fontWeight}`,
}));
</script>

<style scoped>
.insights-button {
  color: var(--textColor);
  background-color: var(--backgroundColor);
  border-color: var(--backgroundColor);
  font-family: var(--fontFamily);
  font-weight: var(fontWeight);
}

.insights-button:hover, .insights-button.active {
  background-color: var(--hoverBackgroundColor);
  color: var(--hoverTextColor);
}

.insights-vr-container {
  background-color: var(--backgroundColor);
}
</style>
